import WebContentLoader, {
  IContentLoaderProps as WebContentLoaderProps,
} from "react-content-loader";
import NativeContentLoader, {
  IContentLoaderProps as NativeContentLoaderProps,
  Rect as NativeRect,
} from "react-content-loader/native";
import {
  ColorTokens,
  FontFamilyTokens,
  GetProps,
  SizeTokens,
  YStack,
  getVariable,
  isWeb,
  withStaticProperties,
} from "tamagui";

import {
  getFontLineHeight,
  getTokenValueOrFallback,
  useGetColor,
} from "@/theme/helpers";
import { isMobileWebkit } from "@/utils/web";

type BaseSkeletonProps = Omit<NativeContentLoaderProps, "widths"> &
  Omit<WebContentLoaderProps, "widths"> &
  Omit<GetProps<typeof YStack>, "backgroundColor" | "foregroundColor"> & {
    backgroundColor?: ColorTokens;
    foregroundColor?: ColorTokens;
  };

interface SkeletonCircleProps extends BaseSkeletonProps {
  size?: SizeTokens | number | string;
}

interface SkeletonTextProps extends BaseSkeletonProps {
  lines?: 1 | 2 | 3 | 4 | 5;
  widths?: string[];
  spacing?: number | string;
  fontFamily?: FontFamilyTokens;
}

const Rect = () =>
  isWeb ? (
    <rect x={0} y={0} width="100%" height="100%" />
  ) : (
    <NativeRect x={0} y={0} width="100%" height="100%" />
  );

const SkeletonRect = ({
  height = "$100",
  width = "100%",
  borderTopLeftRadius = "$default",
  borderTopRightRadius = "$default",
  borderBottomRightRadius = "$default",
  borderBottomLeftRadius = "$default",
  animate = true,
  speed = 2,
  rtl = false,
  preserveAspectRatio = "none",
  backgroundColor = "$contentLoaderBackground" as ColorTokens,
  foregroundColor = "$contentLoaderForeground" as ColorTokens,
  ...props
}: BaseSkeletonProps) => {
  const bgColor = useGetColor(backgroundColor);
  const fgColor = useGetColor(foregroundColor);
  const h = getTokenValueOrFallback(height);
  const w = getTokenValueOrFallback(width);
  const Loader = isWeb ? WebContentLoader : NativeContentLoader;
  // doesn't support relative sizes in viewBox
  let viewBox: string | undefined;
  if (typeof w === "number" && typeof h === "number") {
    viewBox = `0 0 ${w} ${h}`;
  } else if (isMobileWebkit()) {
    // Mobile WebKit requires a defined viewBox for skeleton visibility. This, uh, seems to work.
    viewBox = `0 0 100 100`;
  }

  return (
    <YStack
      borderTopLeftRadius={borderTopLeftRadius}
      borderTopRightRadius={borderTopRightRadius}
      borderBottomRightRadius={borderBottomRightRadius}
      borderBottomLeftRadius={borderBottomLeftRadius}
      overflow="hidden"
      width={w}
      height={h}
      {...props}
    >
      <Loader
        animate={animate}
        speed={speed}
        backgroundColor={bgColor}
        foregroundColor={fgColor}
        aria-hidden
        rtl={rtl}
        viewBox={viewBox}
        preserveAspectRatio={preserveAspectRatio}
      >
        <Rect />
      </Loader>
    </YStack>
  );
};

const SkeletonCircle = ({ size = "$100", ...props }: SkeletonCircleProps) => (
  <SkeletonRect width={size} height={size} borderRadius={100_000} {...props} />
);

const SkeletonText = ({
  lines = 1,
  width = "100%",
  widths = ["95%", "100%", "97%", "92%", "50%"],
  height = "$md",
  spacing = "$100",
  fontFamily = "$sans-serif",
  ...props
}: SkeletonTextProps) => {
  const lineHeight = getFontLineHeight(fontFamily, height);
  const gap = getVariable(spacing);

  const isMultiLine = lines > 1;
  const skeletons = [];
  // 5 line max
  for (let i = 0; i <= 4; i += 1) {
    if (i >= lines) {
      break;
    }

    let lineWidth: string | number;
    if (!isMultiLine) {
      lineWidth = "100%";
      // last line to be shortest
    } else if (i === lines - 1) {
      lineWidth = widths[widths.length - 1];
    } else {
      lineWidth = widths[i];
    }

    skeletons.push(
      <SkeletonRect
        key={i}
        height={lineHeight}
        width={lineWidth}
        borderRadius="$25"
        {...props}
      />,
    );
  }
  return (
    <YStack gap={gap} width={width}>
      {skeletons}
    </YStack>
  );
};

const Skeleton = withStaticProperties(SkeletonRect, {
  Circle: SkeletonCircle,
  Text: SkeletonText,
});

export { Skeleton };
